import { AlertBanner, Button, Card } from '@helix-ds/react'
import { useEffect, type ReactNode } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import { track } from '../../../utils/analytics'
import type { ConsentFlowStep } from '../../../utils/consentFlowHelper'
import type { ConsentLanguageError } from '../hooks/useConsentLanguage'

export const CONSENT_IMAGE = '/images/consent-background.jpg'
export const OPTIONAL_INTRO_IMAGE = '/images/optional-intro.jpg'
export const RESEARCH_PARTICIPATION_IMAGE = '/images/research-participation.jpg'

export function StepPill({ children }: { children: ReactNode }) {
  return (
    <span className="border-border-base bg-bg-secondary text-text-heading inline-flex w-fit rounded-full border px-2 py-1 text-xs font-medium">
      {children}
    </span>
  )
}

function ArrowIcon() {
  return <img src="/icons/arrow-right.svg" alt="" className="h-4 w-4" aria-hidden />
}

export function PrimaryAction({
  children,
  disabled,
  onClick,
}: {
  children: ReactNode
  disabled?: boolean
  onClick?: () => void
}) {
  return (
    <Button
      type="button"
      variant="primary"
      size="lg"
      onClick={onClick}
      disabled={disabled}
      icon={<ArrowIcon />}
      iconPosition="right"
      className="whitespace-nowrap"
    >
      <span>{children}</span>
    </Button>
  )
}

export function ConsentFlowCard({
  children,
  progress,
  imageSrc,
  contentAlign = 'center',
  layout = 'horizontal',
}: {
  children: ReactNode
  progress: ReactNode
  imageSrc: string
  contentAlign?: 'start' | 'center'
  layout?: 'horizontal' | 'vertical'
}) {
  return (
    <div className="flex w-full justify-center font-sans">
      <div className="flex w-full max-w-[900px] flex-col gap-10 pb-10">
        {layout === 'horizontal' ? (
          <section>
            <Card padding="none" className="overflow-hidden">
              <div className="grid md:min-h-[640px] md:grid-cols-[1fr_0.92fr]">
                <div className="bg-bg-secondary min-h-64 md:min-h-[640px]">
                  <img src={imageSrc} alt="" className="h-full w-full object-cover" aria-hidden />
                </div>
                <div
                  className={`flex min-h-[420px] flex-col px-5 py-8 sm:px-8 md:min-h-[640px] ${
                    contentAlign === 'start' ? 'justify-start' : 'justify-center'
                  }`}
                >
                  {children}
                </div>
              </div>
            </Card>
          </section>
        ) : (
          <section>
            <Card padding="none" className="overflow-hidden">
              <div className="bg-bg-secondary h-56 sm:h-72 md:h-80">
                <img src={imageSrc} alt="" className="h-full w-full object-cover" aria-hidden />
              </div>
              <div
                className={`flex flex-col px-5 py-8 sm:px-8 ${
                  contentAlign === 'start' ? 'justify-start' : 'justify-center'
                }`}
              >
                {children}
              </div>
            </Card>
          </section>
        )}
        {progress}
      </div>
    </div>
  )
}

function TimelineItems({ items }: { items: ConsentFlowStep[] }) {
  const intl = useIntl()
  return (
    <ol className="border-border-base space-y-7 border-l pl-4">
      {items.map((item) => (
        <li key={`${item.labelKey}-${item.titleKey}`} className="relative">
          <span
            className="border-background-primary bg-bg-secondary absolute top-[42px] -left-[23px] h-3 w-3 rounded-full border"
            aria-hidden
          />
          <StepPill>{intl.formatMessage({ id: item.labelKey })}</StepPill>
          <h3 className="text-text-heading mt-3 text-base font-semibold">
            {intl.formatMessage({ id: item.titleKey })}
          </h3>
          <p className="text-text-body mt-2 text-sm leading-6">{intl.formatMessage({ id: item.descriptionKey })}</p>
        </li>
      ))}
    </ol>
  )
}

export function WorkflowProgress({ current, next }: { current: ConsentFlowStep[]; next: ConsentFlowStep[] }) {
  return (
    <section className="w-full">
      <Card padding="lg">
        <h2 className="text-text-heading text-2xl leading-9 font-semibold tracking-tight">
          <FormattedMessage id="consentOptions.workflowProgress.currentHeading" defaultMessage="Here's where you are" />
        </h2>
        <div className="mt-6">
          <TimelineItems items={current} />
        </div>
        {next.length > 0 && (
          <>
            <h2 className="text-text-heading mt-8 text-2xl leading-9 font-semibold tracking-tight">
              <FormattedMessage id="consentOptions.workflowProgress.nextHeading" defaultMessage="Up Next" />
            </h2>
            <div className="mt-6">
              <TimelineItems items={next} />
            </div>
          </>
        )}
      </Card>
    </section>
  )
}

export function LoadingPanel({ message }: { message: string }) {
  return (
    <div className="space-y-4">
      <StepPill>
        <FormattedMessage id="consentOptions.loadingPanel.step" defaultMessage="Step 1" />
      </StepPill>
      <p className="text-text-body text-sm leading-6" aria-live="polite">
        {message}
      </p>
    </div>
  )
}

/** Avoid consent-to-treat (Step 1) imagery while consent flow is still unknown - used until context loads. */
export function ContextLoadingScreen() {
  return (
    <div className="flex w-full justify-center px-4 py-16 font-sans">
      <p className="text-text-body text-sm leading-6" aria-live="polite">
        <FormattedMessage id="consentOptions.contextLoading.message" defaultMessage="Loading consent details..." />
      </p>
    </div>
  )
}

export function ConsentLanguageErrorPanel({ error }: { error: ConsentLanguageError | null }) {
  const intl = useIntl()
  useEffect(() => {
    track('consent.load_error', { resource: 'language', status: error ?? 'unknown' })
  }, [error])

  let message: string
  if (error === 'multiple_pending_orders') {
    message = intl.formatMessage({
      id: 'consentOptions.consentLanguageError.multiplePendingOrders',
      defaultMessage:
        'We found more than one order awaiting your consent, so we can’t show the right consent form. Please contact us at support@inflectionmedicine.com and we’ll help you complete it.',
    })
  } else if (error === 'consent_not_applicable') {
    message = intl.formatMessage({
      id: 'consentOptions.consentLanguageError.notApplicable',
      defaultMessage:
        'There’s no consent to testing to complete for your account right now. If you think this is a mistake, contact us at support@inflectionmedicine.com.',
    })
  } else {
    message = intl.formatMessage({
      id: 'consentOptions.consentLanguageError.message',
      defaultMessage:
        'Something went wrong loading your consent information. Please refresh the page to try again, or contact us at support@inflectionmedicine.com for help.',
    })
  }

  return <ErrorPanel message={message} />
}

export function ErrorPanel({ message }: { message: string }) {
  return (
    <div className="space-y-4">
      <StepPill>
        <FormattedMessage id="consentOptions.errorPanel.step" defaultMessage="Step 1" />
      </StepPill>
      <h1 className="text-text-heading text-3xl font-semibold tracking-tight">
        <FormattedMessage id="consentOptions.errorPanel.heading" defaultMessage="Consent" />
      </h1>
      <AlertBanner variant="error">{message}</AlertBanner>
    </div>
  )
}
